import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { RouteComponentProps } from "@reach/router";
import { Brand60 } from "../utils/colors";
import "./PacingTableDashboard.scss";
import { DownloadDropdown, Dropdown, FilterBar, getFormattedValue } from "../Components";
import { useStateFunction } from "../utils/hooks/useData";
import { formatCellValue, simpleDimensionDataFormatter } from "./pacingUtils";
import { CombinedPacingAndBudgetData } from "@blisspointmedia/bpm-types/dist/Pacing";
import MetricsTable from "../SingleChannel/MetricsTable/MetricsTable";
import { DimensionColumn } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import SourcePicker from "../CrossChannel/Components/SourcePicker";
import { exportToExcel } from "../utils/download-utils";
import { Modal } from "react-bootstrap";
import { MdCalendarToday } from "react-icons/md";
import * as R from "ramda";

const columnMetaDataMap = {
  totalSpend: {
    displayName: "Total Spend",
    overlayText:
      "The total amount spent on this row, including spend from platforms or tactics without assigned budgets. This number may be higher than your budget even if you're pacing correctly.",
  },
  budget: {
    displayName: "Budget",
    overlayText: "The total amount of budget allocated to each row.",
  },
  spend: {
    displayName: "Spend Toward Budget",
    overlayText:
      "The amount spent on platforms, funnels, or campaigns within this row that have budgets assigned to them.",
  },
  yesterdaySpend: {
    displayName: "Spend Toward Budget",
    overlayText:
      "The amount spent yesterday on platforms, funnels, or campaigns within this row that have budgets assigned to them.",
  },
};

const COLUMNS_DOLLAR_FORMAT = [
  "totalSpend",
  "totalRevenue",
  "spend",
  "budget",
  "revenue",
  "yesterdaySpend",
  "remainingSpend",
  "totalSpend",
  "totalRevenue",
  "projectedSpend",
];

interface PacingTableDashboardProps {
  pacingTableData: CombinedPacingAndBudgetData[];
  customSegments: string[];
  sourceValueOptions: { label: string; value: string }[];
  platforms: string[];
  tablePreset: string;
  setTablePreset: React.Dispatch<React.SetStateAction<string>>;
  kpi: string;
  setKpi: React.Dispatch<React.SetStateAction<string>>;
  selectedSources: string[];
  setSelectedSources: React.Dispatch<React.SetStateAction<string[]>>;
  cid: string;
  platformAsSourceOfData: boolean;
  groupedTableData: any[];
  pacingTableDataHeaders: any[];
  kpiOptions: {
    label: string;
    value: string;
  }[];
  progress: number;
}

export const PACING_TABLE_PRESET_OPTIONS = [
  { value: "Channel", label: "Channel" },
  { value: "Channel_Platform", label: "Channel, Platform" },
  { value: "Channel_Platform_Funnel", label: "Channel, Platform, Funnel" },
  { value: "Channel_Platform_Campaign", label: "Channel, Platform, Campaign" },
];

const PacingTableDashboard = ({
  pacingTableData,
  customSegments,
  sourceValueOptions,
  platforms,
  tablePreset,
  setTablePreset,
  kpi,
  setKpi,
  selectedSources,
  setSelectedSources,
  cid,
  platformAsSourceOfData,
  groupedTableData,
  pacingTableDataHeaders,
  kpiOptions,
  progress,
}: PacingTableDashboardProps & RouteComponentProps): JSX.Element => {
  const [showEmptyBudgetModal, setShowEmptyBudgetModal] = useState(true);

  const {
    selectedSourceType,
    selectedSourceValues,
    selectedSourceValuePrettyName,
  } = useMemo(() => {
    if (platformAsSourceOfData) {
      return {
        selectedSourceType: "Platform" as const,
        selectedSourceValues: { platform: true },
        selectedSourceValuePrettyName: "Platform",
      };
    } else {
      const sourceValues = selectedSources.reduce((prev, curr) => ({ ...prev, [curr]: true }), {});
      return {
        selectedSourceType: "Source of Truth" as const,
        selectedSourceValues: sourceValues,
        selectedSourceValuePrettyName: sourceValueOptions.reduce(
          (prev, curr) => ({ ...prev, [curr.value]: curr.label }),
          {}
        ),
      };
    }
  }, [selectedSources, sourceValueOptions, platformAsSourceOfData]);

  const [rerenderTable, setRerenderTable] = useState(false);
  const [tableWidth, setTableWidth] = useState(500);
  const [tableHeight, setTableHeight] = useState(300);
  const containerRef = useRef<HTMLDivElement>(null);

  // Table Sizing
  const windowResizeFunction = () => setRerenderTable(true);
  if (typeof window !== "undefined" && window.onresize !== windowResizeFunction) {
    window.onresize = windowResizeFunction;
  }
  useLayoutEffect(() => {
    if (containerRef.current) {
      const newTableHeight = containerRef.current.offsetHeight;
      const newTableWidth = containerRef.current.offsetWidth;
      if (newTableHeight) {
        setTableHeight(newTableHeight);
      }
      if (newTableWidth) {
        setTableWidth(newTableWidth);
      }
    }
    if (rerenderTable) {
      setRerenderTable(false);
    }
  }, [rerenderTable]);

  const [filter, setFilter] = useStateFunction<(line: any) => boolean>(() => true);
  const filterBarOptions = useMemo(() => {
    const uniqueSegmentOptions =
      customSegments?.map(segment => ({
        name: segment,
        label: segment,
      })) ?? [];

    return [...uniqueSegmentOptions];
  }, [customSegments]);

  const filteredData = useMemo(() => {
    return groupedTableData?.filter(filter) || [];
  }, [groupedTableData, filter]);

  const filteredPacingTableDataHeaders = pacingTableDataHeaders.filter(
    row => !tablePreset.split("_").includes(row.name)
  );

  const tableDataForMetrics = useMemo(() => {
    return filteredData.map(row => {
      return filteredPacingTableDataHeaders.map(header => {
        const cellValue = row[header.name] ?? "";
        let label = formatCellValue(cellValue);
        if (R.includes(header.dataVarName, COLUMNS_DOLLAR_FORMAT)) {
          label =
            label === "N/A"
              ? "N/A"
              : getFormattedValue({
                  value: cellValue,
                  type: "$",
                });
        }
        return {
          label,
          value: cellValue,
        };
      });
    });
  }, [filteredData, filteredPacingTableDataHeaders]);

  const pacingTableDataHeadersWithDataVarName = filteredPacingTableDataHeaders
    .map(header => ({
      ...header,
      id: header.id || "unknown_id",
      dataVarName: header.dataVarName || header.id || "unknown_dataVarName",
    }))
    .filter(row => !tablePreset.split("_").includes(row.name));

  const dimensionColumns: DimensionColumn[] = useMemo(() => {
    const dimOptions = tablePreset.split("_");
    return dimOptions.map(row => ({
      id: row,
      dimensionVarName: (row as unknown) as DimensionColumn["dimensionVarName"],
      label: row,
      dimensionTypeName: (row as unknown) as DimensionColumn["dimensionTypeName"],
      divider: false,
    }));
  }, [tablePreset]);

  const dimensionData = useMemo(() => {
    return simpleDimensionDataFormatter({
      data: filteredData,
      tableHeaders: dimensionColumns,
    });
  }, [dimensionColumns, filteredData]);

  const excelDownloadModelOverview = useCallback(() => {
    exportToExcel(pacingTableData, "model_overview");
  }, [pacingTableData]);

  const selectedKpi = useMemo(() => (R.isEmpty(kpi) ? kpiOptions[0].value : kpi), [
    kpi,
    kpiOptions,
  ]);

  return (
    <div className="pacingTableDashboard">
      <div className="pacingTableContainer">
        <div className="pacingTableHeader">
          <div className="pacingTableHeaderButtons">
            <FilterBar
              hasAdvanced={true}
              onFilter={setFilter}
              lines={pacingTableData || []}
              options={filterBarOptions}
            />
            <div className="pacingTableHeaderSettings">
              <div className="pacingTableHeaderDropdownButtons">
                <Dropdown
                  className="pacingTablePresetDropdown"
                  value={tablePreset}
                  onChange={val => setTablePreset(val)}
                  options={PACING_TABLE_PRESET_OPTIONS}
                />
                <SourcePicker
                  selectedSourceType={selectedSourceType}
                  selectedSourceValue={selectedSourceValues}
                  sourceValueOptions={sourceValueOptions.filter(row => row.value === "platform")}
                  applyOnClick={sources => {
                    setSelectedSources(sources);
                  }}
                  platforms={platforms}
                  label={`Source: ${selectedSourceValuePrettyName}`}
                />
                <Dropdown
                  className="pacingTableKpiDropdown"
                  label={kpiOptions.length > 0 ? "KPI" : "No KPI options available"}
                  value={selectedKpi}
                  onChange={val => {
                    if (!R.equals(val, selectedKpi)) {
                      setKpi(val);
                    }
                  }}
                  options={kpiOptions}
                />
                <DownloadDropdown
                  size="sm"
                  onClickOptions={[excelDownloadModelOverview]}
                  menuOptions={["XLSX"]}
                />
              </div>
              <div className="pacingTableHeaderTimeframe">
                <MdCalendarToday color={Brand60} size={22} />
                <span>{progress}% through time period</span>
              </div>
            </div>
          </div>
        </div>

        <div className="metricsTableWrapper" ref={containerRef}>
          {tableDataForMetrics.length > 0 ? (
            <MetricsTable
              aggregateData={[]}
              dimensionColumns={dimensionColumns}
              dimensionData={dimensionData}
              tableData={tableDataForMetrics}
              dataColumns={pacingTableDataHeadersWithDataVarName}
              width={tableWidth}
              height={tableHeight}
              columnMetaDataMap={columnMetaDataMap}
            />
          ) : (
            <Modal
              size="lg"
              show={showEmptyBudgetModal}
              onHide={() => setShowEmptyBudgetModal(!showEmptyBudgetModal)}
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  There are no budgets for these segments, please check the{" "}
                  <a
                    href={`https://blisspoint.tinuiti.com/${cid}/budget-intake-tool`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Budget Intake Tool
                  </a>
                  .
                </Modal.Title>
              </Modal.Header>
            </Modal>
          )}
        </div>
      </div>
    </div>
  );
};

export default PacingTableDashboard;
